import { FC, type ReactNode } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDownload,
  faArrowUpRightFromSquare,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { useSalesCopilotModalStore } from "../../store/salesCopilotModalStore";
import env from "../../utils/env";
import "./salesCopilotModal.css";

interface CopilotOptionCardProps {
  title: ReactNode;
  buttonLabel: string;
  href: string;
  isPrimary?: boolean;
  id?: string;
}

function CopilotOptionCard({
  title,
  buttonLabel,
  href,
  isPrimary = false,
  id,
}: CopilotOptionCardProps) {
  const buttonClass = isPrimary
    ? "copilot-card__button copilot-card__button--primary"
    : "copilot-card__button copilot-card__button--outline";

  return (
    <Box className="copilot-card">
      <Typography
        className="copilot-card__title"
        component="h3"
        variant="h6"
        sx={{
          fontSize: "18px",
          fontWeight: 600,
          marginBottom: "16px",
          color: "#1f1f1f",
          lineHeight: 1.3,
        }}
      >
        {title}
      </Typography>
      <Box className="copilot-card__action" sx={{ marginTop: "auto" }}>
        <a
          href={href}
          id={id}
          target="_blank"
          rel="noopener noreferrer"
          className="copilot-card__link"
          style={{ display: "block", textDecoration: "none" }}
        >
          <Box
            component="span"
            className={buttonClass}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FontAwesomeIcon
              icon={isPrimary ? faDownload : faArrowUpRightFromSquare}
              className="copilot-card__button-icon"
              style={{ width: "16px", height: "16px", marginRight: "8px" }}
            />
            {buttonLabel}
          </Box>
        </a>
      </Box>
    </Box>
  );
}

export const SalesCopilotModal: FC = () => {
  const { isOpen, closeModal } = useSalesCopilotModalStore();

  const chromeExtensionUrl =
    "https://chromewebstore.google.com/detail/madkudu-copilot/jikgjincjapiebekopikcefbkeaimegj";
  const salesforceInstallUrl =
    "https://help.madkudu.com/docs/madkudu-salesforce-package";
  const salesCopilotWebUrl = env.MSI_URL || "#";

  return (
    <Dialog
      open={isOpen}
      onClose={closeModal}
      maxWidth="lg"
      PaperProps={{
        sx: {
          width: "900px",
          maxWidth: "95vw",
          padding: "24px",
          position: "relative",
          backgroundColor: "#f3f7fc",
          fontFamily:
            "Avenir, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
        },
      }}
    >
      <IconButton
        id="sales-copilot-modal-close"
        onClick={closeModal}
        sx={{
          position: "absolute",
          right: "8px",
          top: "8px",
          width: "32px",
          height: "32px",
          color: "#6b7280",
          "&:hover": {
            backgroundColor: "#f3f4f6",
          },
        }}
        aria-label="Close"
      >
        <FontAwesomeIcon
          icon={faXmark}
          style={{ width: "16px", height: "16px" }}
        />
      </IconButton>
      <DialogTitle
        className="copilot-modal__title"
        sx={{
          fontSize: "36px",
          fontWeight: 800,
          marginBottom: "24px",
          color: "#1f1f1f",
          lineHeight: 1,
          padding: 0,
          paddingRight: "40px", // Space for close button
        }}
      >
        Sales Copilot
      </DialogTitle>
      <DialogContent sx={{ padding: 0 }}>
        <Box
          className="copilot-modal__description"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            marginBottom: "32px",
          }}
        >
          <Typography
            className="copilot-modal__text"
            sx={{ fontSize: "18px", lineHeight: 1.5, color: "#6b7280" }}
          >
            Give your sales team the context they need to personalize outreach,
            and get more meetings booked, directly in Salesforce.
          </Typography>
          <Typography
            className="copilot-modal__text"
            sx={{ fontSize: "18px", lineHeight: 1.5, color: "#6b7280" }}
          >
            Sales Copilot surfaces relevant insights in your CRM – such as{" "}
            <span
              className="copilot-modal__highlight"
              style={{ fontWeight: 600, color: "#1f1f1f" }}
            >
              job openings
            </span>
            ,{" "}
            <span
              className="copilot-modal__highlight"
              style={{ fontWeight: 600, color: "#1f1f1f" }}
            >
              tech stack
            </span>
            , and{" "}
            <span
              className="copilot-modal__highlight"
              style={{ fontWeight: 600, color: "#1f1f1f" }}
            >
              recent activity
            </span>{" "}
            within your product or on your website.
          </Typography>
        </Box>

        <Box
          className="copilot-modal__cards"
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: "24px",
          }}
        >
          <CopilotOptionCard
            title={
              <>
                Sales Copilot
                <br />
                Chrome Extension
              </>
            }
            buttonLabel="Install Extension"
            href={chromeExtensionUrl}
            isPrimary
            id="sales-copilot-modal-chrome-extension"
          />
          <CopilotOptionCard
            title="Sales Copilot in Salesforce"
            buttonLabel="Install for Salesforce"
            href={salesforceInstallUrl}
            id="sales-copilot-modal-salesforce"
          />
          <CopilotOptionCard
            title="Sales Copilot Web"
            buttonLabel="Go to Sales Copilot"
            href={salesCopilotWebUrl}
            id="sales-copilot-modal-web"
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};
